import React, { useState, useEffect } from 'react';
import {
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  XMarkIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  CogIcon,
  ExclamationTriangleIcon,
} from '../icons/HeroIcons';
import { PrintfulOrder } from '../../types/order';
import { AISHA_API_BASE } from '../../constants';

interface OrderManagementProps {}

const OrderManagement: React.FC<OrderManagementProps> = () => {
  // State
  const [orders, setOrders] = useState<PrintfulOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('new'); // デフォルトは新規注文
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productName, setProductName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({
    all: 0,
    new: 0,
    processing: 0,
    shipped: 0,
    canceled: 0,
    error: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState<PrintfulOrder | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // タブ定義
  const tabs = [
    { id: 'all', label: '全て', icon: ClipboardDocumentListIcon, color: 'gray' },
    { id: 'new', label: '新規注文', icon: CheckCircleIcon, color: 'blue' },
    { id: 'processing', label: '製造中', icon: CogIcon, color: 'yellow' },
    { id: 'shipped', label: '発送済み', icon: TruckIcon, color: 'green' },
    { id: 'canceled', label: 'キャンセル', icon: XCircleIcon, color: 'orange' },
    { id: 'error', label: 'エラー', icon: ExclamationTriangleIcon, color: 'red' },
  ];

  // 初回読み込み時に自動同期
  useEffect(() => {
    syncOrders();
  }, []);

  // タブ変更時にデータ再取得
  useEffect(() => {
    fetchOrders();
  }, [activeTab, currentPage, searchQuery, dateFrom, dateTo, productName]);

  // 注文一覧を取得
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: activeTab,
        page: currentPage.toString(),
        limit: '20',
        search: searchQuery,
        date_from: dateFrom,
        date_to: dateTo,
        product_name: productName,
      });

      const response = await fetch(`${AISHA_API_BASE}/admin/orders/?${params}`);
      const data = await response.json();

      if (data.success) {
        setOrders(data.orders);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
        setTabCounts(data.tab_counts);
      }
    } catch (error) {
      console.error('注文取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  // Printfulと同期
  const syncOrders = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${AISHA_API_BASE}/admin/orders/sync/`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        console.log(`同期完了: 新規${data.new_count}件, 更新${data.updated_count}件`);
        await fetchOrders();
      }
    } catch (error) {
      console.error('同期エラー:', error);
    } finally {
      setSyncing(false);
    }
  };

  // 注文詳細を表示
  const showOrderDetail = async (order: PrintfulOrder) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  // ステータスのバッジ色を取得
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
      case 'pending':
        return 'bg-blue-900 text-blue-300';
      case 'inprocess':
      case 'onhold':
        return 'bg-yellow-900 text-yellow-300';
      case 'fulfilled':
      case 'partial':
        return 'bg-green-900 text-green-300';
      case 'canceled':
        return 'bg-orange-900 text-orange-300';
      case 'failed':
        return 'bg-red-900 text-red-300';
      default:
        return 'bg-gray-900 text-gray-300';
    }
  };

  // ステータスアイコンを取得
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
      case 'pending':
        return '🔵';
      case 'inprocess':
      case 'onhold':
        return '🟡';
      case 'fulfilled':
      case 'partial':
        return '🟢';
      case 'canceled':
        return '🟠';
      case 'failed':
        return '🔴';
      default:
        return '⚪';
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <ClipboardDocumentListIcon className="w-8 h-8 mr-2" />
          注文管理
        </h2>
        <button
          onClick={syncOrders}
          disabled={syncing}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            syncing
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          <ArrowPathIcon className={`w-5 h-5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? '同期中...' : '手動同期'}
        </button>
      </div>

      {/* タブ */}
      <div className="flex space-x-2 border-b border-gray-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setCurrentPage(1);
              }}
              className={`flex items-center px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? `text-${tab.color}-400 border-${tab.color}-400`
                  : 'text-gray-400 border-transparent hover:text-gray-300'
              }`}
            >
              <Icon className="w-5 h-5 mr-2" />
              {tab.label}
              <span className="ml-2 px-2 py-0.5 bg-gray-700 rounded-full text-xs">
                {tabCounts[tab.id] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* 検索・フィルター */}
      <div className="bg-gray-800 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* 検索 */}
          <div className="relative">
            <input
              type="text"
              placeholder="注文番号・顧客名で検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>

          {/* 日付範囲（開始） */}
          <div className="relative">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <CalendarIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>

          {/* 日付範囲（終了） */}
          <div className="relative">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <CalendarIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>

          {/* 商品名 */}
          <div>
            <input
              type="text"
              placeholder="商品名で検索"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* フィルタークリア */}
        {(searchQuery || dateFrom || dateTo || productName) && (
          <button
            onClick={() => {
              setSearchQuery('');
              setDateFrom('');
              setDateTo('');
              setProductName('');
            }}
            className="text-sm text-indigo-400 hover:text-indigo-300"
          >
            フィルターをクリア
          </button>
        )}
      </div>

      {/* 注文一覧テーブル */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">読み込み中...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">注文が見つかりません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    注文ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    注文日時
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    顧客名
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    商品名
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    金額
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-700 transition-colors">
                    <td className="px-4 py-4 text-sm text-white">
                      {order.internal_order_id || `#${order.printful_order_id}`}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-300">
                      {new Date(order.created_at).toLocaleString('ja-JP', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-4 text-sm text-white">
                      {order.customer_name}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-300">
                      <div className="flex items-center">
                        <div className="flex -space-x-2 mr-3">
                          {/* 商品画像を表示（最初の商品のみ） */}
                          {order.product_info && order.product_info[0]?.product?.image && (
                            <img
                              src={order.product_info[0].product.image}
                              alt={`${order.product_name} - 商品`}
                              className="w-8 h-8 rounded border border-gray-600 object-cover"
                              title="商品画像"
                            />
                          )}
                          {/* プレビュー画像がある場合は優先表示 */}
                          {order.product_info && order.product_info[0]?.files?.find(f => f.type === 'preview')?.thumbnail_url ? (
                            <img
                              src={order.product_info[0].files.find(f => f.type === 'preview')?.thumbnail_url}
                              alt={`${order.product_name} - プレビュー`}
                              className="w-8 h-8 rounded border border-gray-600 object-cover"
                              title="プレビュー"
                            />
                          ) : order.thumbnail_url ? (
                            <img
                              src={order.thumbnail_url}
                              alt={`${order.product_name} - デザイン`}
                              className="w-8 h-8 rounded border border-gray-600 object-cover"
                              title="カスタムデザイン"
                            />
                          ) : null}
                        </div>
                        <div>
                          <div>{order.product_name}</div>
                          {order.product_info && order.product_info.length > 0 && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {order.product_info.reduce((total, item) => total + (item.quantity || 0), 0)}個
                              {order.product_info.length > 1 && ` (${order.product_info.length}種類)`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-white font-medium">
                      ¥{Math.floor(order.total_cost).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)} {order.status_text}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <button
                        onClick={() => showOrderDetail(order)}
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {totalPages > 1 && (
          <div className="bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-700">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                前へ
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                次へ
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-400">
                  全 <span className="font-medium text-white">{totalCount}</span> 件中{' '}
                  <span className="font-medium text-white">
                    {(currentPage - 1) * 20 + 1}
                  </span>{' '}
                  -{' '}
                  <span className="font-medium text-white">
                    {Math.min(currentPage * 20, totalCount)}
                  </span>{' '}
                  件を表示
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-600 bg-gray-800 text-sm font-medium text-white">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 注文詳細モーダル */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                注文詳細 {selectedOrder.internal_order_id || `#${selectedOrder.printful_order_id}`}
              </h3>
              <div className="flex items-center space-x-2">
                {/* キャンセルボタン（条件付き表示） */}
                {['draft', 'pending', 'inprocess', 'onhold'].includes(selectedOrder.status) && (
                  <button
                    onClick={async () => {
                      if (!confirm('この注文をキャンセルしますか？\n\nこの操作は取り消せません。')) return;
                      
                      try {
                        const response = await fetch(`${AISHA_API_BASE}/admin/orders/${selectedOrder.internal_order_id}/cancel/`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' }
                        });
                        
                        const data = await response.json();
                        
                        if (data.success) {
                          alert('注文がキャンセルされました');
                          setShowDetailModal(false);
                          setSelectedOrder(null);
                          await fetchOrders(); // リストを更新
                        } else {
                          alert(`キャンセルに失敗しました: ${data.error}`);
                        }
                      } catch (error) {
                        console.error('Cancel order error:', error);
                        alert('キャンセル処理中にエラーが発生しました');
                      }
                    }}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    キャンセル
                  </button>
                )}
                
                {/* 閉じるボタン */}
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* 基本情報 */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">基本情報</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Printful No:</span>
                    <span className="ml-2 text-white">{selectedOrder.printful_order_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">ステータス:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                      {getStatusIcon(selectedOrder.status)} {selectedOrder.status_text}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">作成日時:</span>
                    <span className="ml-2 text-white">
                      {new Date(selectedOrder.created_at).toLocaleString('ja-JP')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">更新日時:</span>
                    <span className="ml-2 text-white">
                      {new Date(selectedOrder.updated_at).toLocaleString('ja-JP')}
                    </span>
                  </div>
                  {selectedOrder.shipped_at && (
                    <div>
                      <span className="text-gray-400">発送日時:</span>
                      <span className="ml-2 text-white">
                        {new Date(selectedOrder.shipped_at).toLocaleString('ja-JP')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 顧客情報 */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">顧客情報</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">名前:</span>
                    <span className="ml-2 text-white">{selectedOrder.customer_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">メール:</span>
                    <span className="ml-2 text-white">{selectedOrder.customer_email}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">ユーザーID:</span>
                    <span className="ml-2 text-white font-mono text-xs">{selectedOrder.user_id}</span>
                  </div>
                </div>
              </div>

              {/* 商品情報 */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">商品情報</h4>
                {selectedOrder.product_info && selectedOrder.product_info.length > 0 ? (
                  <div className="space-y-4">
                    {selectedOrder.product_info.map((item, index) => (
                      <div key={item.id || index} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex items-start space-x-4">
                          {/* 商品画像とカスタム画像を並べて表示 */}
                          <div className="flex space-x-2">
                            {/* 商品画像 */}
                            {item.product?.image && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">商品</p>
                                <img
                                  src={item.product.image}
                                  alt={`${item.name} - 商品`}
                                  className="w-20 h-20 rounded-lg object-cover border border-gray-600"
                                />
                              </div>
                            )}
                            {/* カスタムデザイン（アップロード画像） */}
                            {item.files && item.files.length > 0 && (
                              <>
                                {/* デザイン元画像 */}
                                {item.files.find(f => f.type === 'default')?.thumbnail_url && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">デザイン</p>
                                    <img
                                      src={item.files.find(f => f.type === 'default')?.thumbnail_url}
                                      alt={`${item.name} - デザイン`}
                                      className="w-20 h-20 rounded-lg object-cover border border-gray-600"
                                    />
                                  </div>
                                )}
                                {/* プレビュー画像（モックアップ） */}
                                {item.files.find(f => f.type === 'preview')?.thumbnail_url && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">プレビュー</p>
                                    <img
                                      src={item.files.find(f => f.type === 'preview')?.thumbnail_url}
                                      alt={`${item.name} - プレビュー`}
                                      className="w-20 h-20 rounded-lg object-cover border border-gray-600"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{item.name}</p>
                            <div className="mt-2 flex items-center space-x-4 text-sm">
                              <div>
                                <span className="text-gray-400">数量:</span>
                                <span className="ml-2 text-white font-semibold">{item.quantity}個</span>
                              </div>
                              {item.retail_price && (
                                <div>
                                  <span className="text-gray-400">単価:</span>
                                  <span className="ml-2 text-white">¥{parseFloat(item.retail_price).toLocaleString()}</span>
                                </div>
                              )}
                              {item.retail_price && (
                                <div>
                                  <span className="text-gray-400">小計:</span>
                                  <span className="ml-2 text-white font-semibold">
                                    ¥{(parseFloat(item.retail_price) * item.quantity).toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                            {item.sku && (
                              <p className="mt-1 text-xs text-gray-400">SKU: {item.sku}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* 価格サマリー */}
                    <div className="mt-4 pt-4 border-t border-gray-600 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">商品合計:</span>
                        <span className="text-white">¥{Math.floor(selectedOrder.retail_price).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">送料:</span>
                        <span className="text-white">¥{Math.floor(selectedOrder.shipping_cost).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">税金:</span>
                        <span className="text-white">¥{Math.floor(selectedOrder.tax).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-600">
                        <span className="text-gray-400 font-semibold">合計金額:</span>
                        <span className="text-white font-bold text-lg">¥{Math.floor(selectedOrder.total_cost).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // フォールバック: product_infoがない場合は従来の表示
                  <div className="flex items-start space-x-4">
                    {selectedOrder.thumbnail_url && (
                      <img
                        src={selectedOrder.thumbnail_url}
                        alt={selectedOrder.product_name}
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{selectedOrder.product_name}</p>
                      <div className="mt-2 space-y-1 text-sm">
                        <div>
                          <span className="text-gray-400">商品価格:</span>
                          <span className="ml-2 text-white">¥{Math.floor(selectedOrder.retail_price).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">送料:</span>
                          <span className="ml-2 text-white">¥{Math.floor(selectedOrder.shipping_cost).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">税金:</span>
                          <span className="ml-2 text-white">¥{Math.floor(selectedOrder.tax).toLocaleString()}</span>
                        </div>
                        <div className="pt-2 border-t border-gray-700">
                          <span className="text-gray-400">合計:</span>
                          <span className="ml-2 text-white font-bold text-lg">¥{Math.floor(selectedOrder.total_cost).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedOrder.memo && (
                  <div className="mt-3 bg-gray-700 rounded-lg p-3">
                    <span className="text-gray-400 text-sm">メモ:</span>
                    <p className="text-white text-sm mt-1 whitespace-pre-wrap">{selectedOrder.memo}</p>
                  </div>
                )}
              </div>

              {/* 利益計算 */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">利益計算</h4>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="space-y-3">
                    {/* 商品ごとの利益計算 */}
                    {selectedOrder.product_info && selectedOrder.product_info.map((item: any, idx: number) => {
                      // costs_detailから原価情報を取得
                      const costsDetail = selectedOrder.costs_detail || {};
                      
                      // 新しい構造からPrintful原価を取得
                      const itemPrice = parseFloat(item.price) || 0;  // 商品ごとのPrintful原価
                      const itemRetailPrice = parseFloat(item.retail_price) || 0;  // 商品ごとの販売価格
                      const itemProfit = parseFloat(item.profit_per_unit) || (itemRetailPrice - itemPrice);  // 単価利益
                      
                      const retailPrice = Math.floor(itemRetailPrice);
                      const quantity = item.quantity || 1;
                      
                      // 実際の原価と利益
                      const actualCost = Math.floor(itemPrice);
                      const actualProfit = Math.floor(itemProfit);
                      
                      return (
                        <div key={idx} className="pb-3 border-b border-gray-600 last:border-0">
                          <div className="text-sm text-gray-300 mb-2">{item.name}</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-400">販売単価:</span>
                              <span className="ml-2 text-white">¥{retailPrice.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Printful原価:</span>
                              <span className="ml-2 text-gray-300">¥{actualCost.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">数量:</span>
                              <span className="ml-2 text-white">{quantity}個</span>
                            </div>
                            <div>
                              <span className="text-gray-400">利益単価:</span>
                              <span className="ml-2 text-green-400">¥{actualProfit.toLocaleString()}</span>
                            </div>
                            <div className="col-span-2 pt-2 border-t border-gray-600">
                              <span className="text-gray-400">商品利益合計:</span>
                              <span className="ml-2 text-green-400 font-semibold">
                                ¥{(actualProfit * quantity).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* 送料の利益 */}
                    <div className="pt-3 border-t border-gray-600">
                      <div className="text-sm">
                        <div className="mb-2">
                          <span className="text-gray-400">送料請求額:</span>
                          <span className="ml-2 text-white">¥{Math.floor(selectedOrder.shipping_cost).toLocaleString()}</span>
                        </div>
                        <div className="mb-2">
                          <span className="text-gray-400">Printful送料原価:</span>
                          <span className="ml-2 text-gray-300">
                            ¥{Math.floor(parseFloat(selectedOrder.costs_detail?.printful_shipping) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">送料上乗せ利益:</span>
                          <span className="ml-2 text-green-400">
                            ¥{(() => {
                              const printfulShipping = parseFloat(selectedOrder.costs_detail?.printful_shipping) || 0;
                              const retailShipping = parseFloat(selectedOrder.shipping_cost) || 0;
                              return Math.floor(retailShipping - printfulShipping).toLocaleString();
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* 総利益 */}
                    <div className="pt-3 border-t border-gray-600">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300 font-semibold">総利益:</span>
                        <span className="text-green-400 text-lg font-bold">
                          ¥{(() => {
                            // 商品利益の合計
                            let productProfit = 0;
                            if (selectedOrder.product_info) {
                              selectedOrder.product_info.forEach((item: any) => {
                                const itemPrice = parseFloat(item.price) || 0;
                                const itemRetailPrice = parseFloat(item.retail_price) || 0;
                                const quantity = item.quantity || 1;
                                productProfit += (itemRetailPrice - itemPrice) * quantity;
                              });
                            }
                            
                            // 送料利益
                            const printfulShipping = parseFloat(selectedOrder.costs_detail?.printful_shipping) || 0;
                            const retailShipping = parseFloat(selectedOrder.shipping_cost) || 0;
                            const shippingProfit = retailShipping - printfulShipping;
                            
                            const totalProfit = productProfit + shippingProfit;
                            return Math.floor(totalProfit).toLocaleString();
                          })()}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        <div>内訳: 商品利益 ¥{(() => {
                          let productProfit = 0;
                          if (selectedOrder.product_info) {
                            selectedOrder.product_info.forEach((item: any) => {
                              const itemPrice = parseFloat(item.price) || 0;
                              const itemRetailPrice = parseFloat(item.retail_price) || 0;
                              const quantity = item.quantity || 1;
                              productProfit += (itemRetailPrice - itemPrice) * quantity;
                            });
                          }
                          return Math.floor(productProfit).toLocaleString();
                        })()} + 送料利益 ¥{(() => {
                          const printfulShipping = parseFloat(selectedOrder.costs_detail?.printful_shipping) || 0;
                          const retailShipping = parseFloat(selectedOrder.shipping_cost) || 0;
                          return Math.floor(retailShipping - printfulShipping).toLocaleString();
                        })()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 配送情報 */}
              {selectedOrder.recipient_info && (
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">配送先情報</h4>
                  <div className="bg-gray-700 rounded-lg p-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <span className="text-gray-400 w-20">氏名:</span>
                        <span className="text-white font-medium">{selectedOrder.recipient_info.name}</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-400 w-20">郵便番号:</span>
                        <span className="text-gray-300">〒{selectedOrder.recipient_info.zip}</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-400 w-20">住所:</span>
                        <div className="text-gray-300">
                          {selectedOrder.recipient_info.state_name || selectedOrder.recipient_info.state_code} {selectedOrder.recipient_info.city}<br />
                          {selectedOrder.recipient_info.address1}
                          {selectedOrder.recipient_info.address2 && (
                            <><br />{selectedOrder.recipient_info.address2}</>
                          )}
                        </div>
                      </div>
                      {selectedOrder.recipient_info.phone && (
                        <div className="flex items-start">
                          <span className="text-gray-400 w-20">電話番号:</span>
                          <span className="text-gray-300">{selectedOrder.recipient_info.phone}</span>
                        </div>
                      )}
                      {selectedOrder.recipient_info.email && (
                        <div className="flex items-start">
                          <span className="text-gray-400 w-20">Email:</span>
                          <span className="text-gray-300">{selectedOrder.recipient_info.email}</span>
                        </div>
                      )}
                      {selectedOrder.recipient_info.country_name && (
                        <div className="flex items-start">
                          <span className="text-gray-400 w-20">国:</span>
                          <span className="text-gray-300">{selectedOrder.recipient_info.country_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* トラッキング情報 */}
              {selectedOrder.tracking_number && (
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">配送追跡</h4>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-400">追跡番号:</p>
                    <p className="text-white font-mono">{selectedOrder.tracking_number}</p>
                    {selectedOrder.tracking_url && (
                      <a
                        href={selectedOrder.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center mt-2 text-indigo-400 hover:text-indigo-300"
                      >
                        追跡ページを開く
                        <TruckIcon className="w-4 h-4 ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;